import React, { useState } from 'react';
import { ChevronUp, Plus } from 'lucide-react';
import clinicImg from '../../assets/clinicReceptionImg.jpg';

const ServiceAccordianSection = ({ services, serviceName }) => {
  const [openAccordion, setOpenAccordion] = useState(services[0].service.toLowerCase());

  const toggleAccordion = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  // Create accordion item component to reduce repetition
  const AccordionItem = ({ id, title, description }) => {
    const isOpen = openAccordion === id;
    
    return (
      <div 
        className={`border rounded-md overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'bg-teal-50 shadow-md' : 'bg-white border-teal-100'}`}
      >
        <button
          className="w-full p-4 flex justify-between items-center text-left transition-colors duration-200"
          onClick={() => toggleAccordion(id)}
        >
          <span className={`font-medium ${isOpen ? 'text-teal-600' : 'text-gray-700'}`}>{title}</span>
          <div className={`transform transition-transform duration-300 ${isOpen ? 'rotate-0' : 'rotate-90'}`}>
            {isOpen ? <ChevronUp size={20} className="text-teal-500" /> : <Plus size={20} className="text-lime-500" />}
          </div>
        </button>
        
        <div 
          className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-40' : 'max-h-0'}`}
        >
          <div className="p-4 pt-0">
            <p className="text-gray-700">
              {description}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative font-sans bg-gradient-to-br from-white via-teal-50 to-lime-50">
      {/* Clean, non-repetitive background with abstract shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          {/* Abstract curved shape in top-left */}
          <path 
            d="M-100,0 C50,-50 150,150 300,100 C450,50 350,250 500,200" 
            fill="none" 
            stroke="#0AB" 
            strokeWidth="3" 
            strokeOpacity="0.07" 
          />
          
          {/* Abstract curved shape in bottom-right */}
          <path 
            d="M800,400 C950,450 1050,350 1200,450 C1350,550 1450,450 1600,500" 
            fill="none" 
            stroke="#8D2" 
            strokeWidth="3" 
            strokeOpacity="0.07" 
          />
          
          {/* Single large circular element */}
          <circle cx="90%" cy="15%" r="120" fill="#0AB" fillOpacity="0.03" />
          
          {/* Heart-inspired element */}
          <path 
            d="M200,400 C220,380 260,380 270,420 C280,460 240,470 200,440 C160,470 120,460 130,420 C140,380 180,380 200,400" 
            fill="#8D2" 
            fillOpacity="0.05" 
          />
        </svg>
      </div>
      
      {/* Main Content */}
      <div className="flex flex-col md:flex-row w-full max-w-6xl mx-auto p-4 md:p-8 gap-4 md:gap-8 relative z-10">
        {/* Left Section */}
        <div className="w-full md:w-1/2 p-4 bg-white/70 backdrop-blur-sm rounded-lg">
          <h1 className="text-3xl md:text-4xl font-medium text-teal-700 mb-4 md:mb-8">
            Welcome to your <span className="text-lime-600">{serviceName}</span> dental visit
          </h1>
          
          <p className="text-gray-700 mb-6">
            Come on in! Use an iPad to check in and fill out your patient forms. We'll escort you
            back to a patient room soon — our average wait time is around 5 minutes.
          </p>
          
          <div className="mt-6 mb-4 relative overflow-hidden rounded-lg shadow-lg group">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-lime-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <img 
              alt="Dental office receptionist helping patient" 
              className="rounded-lg w-full transition-transform duration-500 group-hover:scale-105"
              src={clinicImg}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-teal-600/80 to-transparent p-4">
              <p className="text-white text-sm md:text-base font-medium">Pristine Smile Dental Care</p>
            </div>
          </div>
        </div>
        
        {/* Right Section */}
        <div className="w-full md:w-1/2 p-4 bg-white/70 backdrop-blur-sm rounded-lg">
          <h1 className="text-3xl md:text-4xl font-medium text-teal-700 mb-4 md:mb-6">
            Choose Your <span className="text-lime-600">Enhancements</span>
          </h1>
          
          <p className="text-gray-700 mb-6">
            Work 1-on-1 with your dentist to pick the best {serviceName} treatment to meet your
            vision:
          </p>
          
          {/* Animated Accordion Section */}
          <div className="mt-6 space-y-4">
            {
              services.map((service, index) => 
                <AccordionItem 
                  key={index}
                  id={service.service.toLowerCase()} 
                  title={service.service}
                  description={service.description} />)
            }
          </div>
        </div>
      </div>
      
      {/* Clean footer with single wave */}
      <div className="w-full h-16 md:h-24 overflow-hidden relative mt-8">
        <svg className="absolute bottom-0 w-full h-full" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path 
            d="M0,60 C300,0 600,120 1200,50 L1200,120 L0,120 Z" 
            className="fill-teal-500 opacity-10"></path>
          <path 
            d="M0,80 C400,30 800,140 1200,70 L1200,120 L0,120 Z" 
            className="fill-lime-500 opacity-10"></path>
        </svg>
      </div>
    </div>
  );
};

export default ServiceAccordianSection;